//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState
} from 'react';

import { Box, Menu, MenuItem } from '@mui/material';

import HeaderControl from './HeaderControl';
import PanelPill from './PanelPill';
import { orderTools, resolveRailFit } from './pillFit';
import { runningDescription, type ToolEntry } from './toolEntries';
import { type PinnedTools } from './usePinnedTools';
import { toolLauncherId } from '@/components/Panels/panelIds';
import { usePanels } from '@/hooks/usePanels';
import { useUI } from '@/hooks/useUI';

// The space between pills, which the fitting arithmetic has to account for
// but a measured pill does not include.
const PILL_GAP = 4;

// The `+N` control, which is a pill of the same construction and always has
// something before it, so it is charged for the gap it follows.
const OVERFLOW_WIDTH = 60 + PILL_GAP;

// Every width here is an integer the browser rounded, and the sum of n
// rounded parts can exceed the rounded whole they were measured inside by
// about half a pixel each.
const roundingSlack = (count: number): number => Math.ceil(count / 2) + 1;

// What a flyout row says about itself beyond its name: why it refuses, or
// that it is running out of sight. The reason wins, since a tool that cannot
// be opened has nothing useful to say about its lifecycle. Running shares its
// phrasing and its mounted-and-hidden gate with the pill and the Tools list,
// through the one helper in toolEntries.ts, so a tool reads the same
// wherever its state is drawn.
const rowDescription = (
  tool: ToolEntry, isMounted: boolean, isOpen: boolean
): string | undefined => {
  const { name, isDisabled, disabledReason } = tool;
  if (isDisabled && disabledReason) return `${name} — ${disabledReason}`;
  return runningDescription(name, isMounted, isOpen);
};

/**
 * The header's taskbar: the tools a user pinned, plus whatever is running.
 *
 * Pinned items are the shortcuts a user chose to keep. Running items are here
 * whether or not they were pinned, because a panel that is iconified has
 * nowhere else to live -- hiding one would otherwise lose it. Everything else
 * lives in the Tools list and is not drawn here at all.
 *
 * Running leads, then pinned. That does mean a pinned pill shifts right when
 * something starts running, which is the price of putting the live thing
 * first; the movement follows the user's own action rather than arriving
 * unbidden.
 *
 * Nothing here is ever drawn as a glyph alone. A pill that does not fit moves
 * into the `+N` flyout with its name intact.
 */
const HeaderTaskbar = (
  { tools, pinned }: { tools: ToolEntry[]; pinned: PinnedTools }
) => {
  const { isPanelMounted, isPanelOpen } = usePanels();
  const { displayMode } = useUI();

  const slotRef = useRef<HTMLDivElement | null>(null);
  const pillRefs = useRef(new Map<string, HTMLElement>());
  // Null until the slot has been measured. Zero is a real answer -- a header
  // with nothing left to give -- so the two cannot share a value.
  const [available, setAvailable] = useState<number | null>(null);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(
    null);

  const ranked = useMemo(() => {
    const running = (tool: ToolEntry) =>
      tool.panelId !== undefined && isPanelMounted(tool.panelId);
    const open = (tool: ToolEntry) =>
      tool.panelId !== undefined && isPanelOpen(tool.panelId);
    return orderTools(tools.map((tool) => ({
      tool,
      id: tool.id,
      isRunning: running(tool),
      isOpen: open(tool),
      isPinned: pinned.isPinned(tool.id)
    }))).filter((item) => item.isRunning || item.isPinned);
  }, [tools, pinned, isPanelMounted, isPanelOpen]);

  const layout = useMemo(
    () => resolveRailFit(
      ranked.map((item) => widths[item.id] ?? 0),
      available === null ?
        null : available + roundingSlack(ranked.length),
      OVERFLOW_WIDTH, PILL_GAP),
    [ranked, widths, available]);

  // The slot exists only while there is something to draw, so the observer
  // has to be re-attached when the rail comes back from empty. That is the
  // only re-attachment there is: a tool joining or leaving an occupied rail
  // keeps the same slot element, and the observer with it.
  const hasTools = ranked.length > 0;

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;

    const measure = () => setAvailable(slot.clientWidth);
    const watcher = new ResizeObserver(measure);
    watcher.observe(slot);
    measure();
    return () => watcher.disconnect();
  }, [hasTools]);

  // The `+N` control and its menu unmount together when everything fits, and
  // nothing else clears the anchor they were holding: kept, it would remount
  // the menu already open on the next narrowing, laid out against a control
  // that left the document.
  useEffect(() => {
    if (layout.visible >= ranked.length) setOverflowAnchor(null);
  }, [layout.visible, ranked.length]);

  // A pill in the flyout is unmounted, so a mode change cannot re-measure it
  // where it stands: its stored width would go on charging for a label it no
  // longer draws, and it could never earn its way back onto the rail. Dropping
  // every width returns the rail to its unmeasured state for one frame -- every
  // pill drawn, so every pill measurable -- which is how it starts up anyway.
  useLayoutEffect(() => { setWidths({}); }, [displayMode]);

  useLayoutEffect(() => {
    setWidths((previous) => {
      let changed = false;
      const next = { ...previous };
      for (const item of ranked) {
        const element = pillRefs.current.get(item.id);
        if (!element) continue;
        const measured = element.offsetWidth;
        if (measured > 0 && next[item.id] !== measured) {
          next[item.id] = measured;
          changed = true;
        }
      }
      return changed ? next : previous;
    });
    // Whenever the drawn set changes, not only when the ranking does: a pill
    // that was in the flyout can only be measured on the frame it is drawn,
    // which is the frame `layout.visible` grew to include it.
  }, [ranked, displayMode, layout.visible]);

  const setPillRef = useCallback(
    (id: string) => (element: HTMLElement | null) => {
      if (element) pillRefs.current.set(id, element);
      else pillRefs.current.delete(id);
    }, []);

  // Nothing pinned and nothing running: no taskbar rather than an empty
  // landmark and a gap in the header.
  if (ranked.length === 0) return null;

  const visible = ranked.slice(0, layout.visible);
  const hidden = ranked.slice(layout.visible);

  // Handed the `+N` control the flyout hangs off rather than the row that was
  // clicked. A tool that anchors a menu of its own -- demo data is the only
  // one -- would otherwise anchor it to a row that unmounts with the closing
  // flyout, and be left pointing at a detached node.
  const openFromFlyout = (tool: ToolEntry) => {
    const control = overflowAnchor;
    if (!control) return;
    setOverflowAnchor(null);
    tool.open(control);
  };

  const drawPill = (tool: ToolEntry) => tool.panelId === undefined ? (
    // No panel, so no lifecycle: this one is always ghost.
    <HeaderControl
      id={toolLauncherId(tool.id)}
      icon={tool.icon}
      label={tool.label}
      // A tool with no glyph keeps its name, or the mode would draw a button
      // with nothing in it at all.
      isLabelHidden={displayMode === 'icons' && Boolean(tool.icon)}
      isIconHidden={displayMode === 'titles'}
      description={tool.name}
      isDisabled={tool.isDisabled}
      popup={tool.popup}
      isExpanded={tool.isExpanded}
      onClick={(event) => tool.open(event.currentTarget)}
    />
  ) : (
    <PanelPill
      panelId={tool.panelId}
      label={tool.label}
      isIconOnly={displayMode === 'icons'}
      isTitleOnly={displayMode === 'titles'}
      isDisabled={tool.isDisabled}
      disabledReason={tool.disabledReason}
    />
  );

  return (
    <Box
      ref={slotRef}
      data-taskbar-slot
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        // The slot is the measurement. `1 1 0` sizes it from the room flex
        // has left over and `overflow: hidden` stops its contents spilling
        // past it, so its width is what the header can spare and never what
        // the rail drew. Measuring anything that answers back is what
        // produced two stable layouts at one viewport width, each reachable
        // only by resizing toward it from one side.
        //
        // The floor is the flyout's own width: below that the rail would be
        // drawing a `+N` the slot then clipped to nothing -- present to a
        // screen reader, unreachable by a pointer. A constant, so it stays a
        // fact about the control rather than an answer read back from the
        // fit that a narrower slot would then change.
        flex: '1 1 0',
        minWidth: `${OVERFLOW_WIDTH}px`,
        overflow: 'hidden'
      }}
    >
      <Box
        // A group rather than a toolbar: the toolbar role promises a single
        // tab stop with arrow-key movement between the controls, and every
        // pill here is its own tab stop. A role that over-promises sends a
        // screen-reader user hunting for arrow keys that do nothing.
        role="group"
        aria-label="Pinned and running tools"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: `${PILL_GAP}px`,
          flexShrink: 0
        }}
      >
        {visible.map((item) => (
          <Box
            key={item.id}
            component="span"
            ref={setPillRef(item.id)}
            sx={{ display: 'inline-flex', flexShrink: 0 }}
          >
            {drawPill(item.tool)}
          </Box>
        ))}

        {hidden.length > 0 && (
          <>
            <HeaderControl
              icon={null}
              // Its label is a count rather than a name, so it stands where
              // the others put a glyph and at that size: this is the control
              // a user aims at when the rail has run out of room, and it was
              // the smallest thing in the row.
              isLabelGlyph
              label={`+${hidden.length}`}
              accessibleName={`+${hidden.length} more ` +
                              `${hidden.length === 1 ? 'tool' : 'tools'}`}
              popup="menu"
              isExpanded={overflowAnchor !== null}
              onClick={(event) => setOverflowAnchor(event.currentTarget)}
            />
            <Menu
              open={overflowAnchor !== null}
              anchorEl={overflowAnchor}
              onClose={() => setOverflowAnchor(null)}
            >
              {hidden.map(({ tool, isRunning, isOpen }) => {
                // Mounted alone is what keeps a pill on the bar; whether it
                // is also open decides what this row says about it, the same
                // gate the description below applies.
                const isHiddenRunning = isRunning && !isOpen;
                return (
                  <MenuItem
                    key={tool.id}
                    // A tool the rail would refuse must not become openable
                    // for having been squeezed off it.
                    disabled={tool.isDisabled}
                    // The state a pill in the rail draws as enclosure, and
                    // the reason a disabled one refuses. Running is said
                    // twice over: once in the row for anyone reading it, and
                    // once as the row's description for anyone who is not.
                    // The visible half is hidden from assistive technology
                    // so it is not announced twice, and the name stays the
                    // tool's own -- a name that changes as a panel mounts is
                    // its own defect.
                    title={rowDescription(tool, isRunning, isOpen)}
                    onClick={() => openFromFlyout(tool)}
                  >
                    {tool.icon && (
                      <Box
                        aria-hidden
                        component="span"
                        sx={{ display: 'inline-flex', marginRight: 1 }}
                      >
                        {tool.icon}
                      </Box>
                    )}
                    {tool.name}
                    {isHiddenRunning && (
                      <Box
                        aria-hidden
                        component="span"
                        sx={{
                          marginLeft: 'auto',
                          paddingLeft: 2,
                          fontSize: '11px',
                          // A word, not a dot: the state has to survive a
                          // reader who cannot tell the color apart.
                          color: 'text.secondary'
                        }}
                      >
                        running
                      </Box>
                    )}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};

export default HeaderTaskbar;
